import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Target, ScanLine, Headphones, Palette, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Home' },
  { path: '/leads/pipeline', icon: Target, label: 'Leads' },
  // Scan FAB is rendered separately in the center
  { path: '/service/helpdesk', icon: Headphones, label: 'Service' },
];

export const BottomNav = ({ onNavigate, onOpenMenu }) => {
  const { isViewingAs, effectiveRole } = useAuth();

  // Creators get "Creative" shortcut, non-creators get "Contacts"
  const isCreator = effectiveRole !== 'team_member' || true; // All roles that can access creative studio
  const lastNavItem = isCreator
    ? { path: '/content', icon: Palette, label: 'Creative', testId: 'bottom-nav-creative' }
    : { path: '/leads/pipeline', icon: Users, label: 'Contacts', testId: 'bottom-nav-contacts' };

  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 bg-charcoal z-50 md:hidden safe-area-bottom"
      )}
      data-testid="bottom-nav"
    >
      {isViewingAs && (
        <div className="bg-amber-500/15 px-3 py-1 text-center">
          <span className="text-[10px] text-amber-400 font-medium">
            Viewing as {effectiveRole === 'country_operator' ? 'Operator' : 'Client'}
          </span>
        </div>
      )}
      <div className="flex items-center justify-around h-16 px-1 relative">
        {/* Home */}
        <NavLink
          to={navItems[0].path}
          onClick={onNavigate}
          className={({ isActive }) => cn(
            "flex flex-col items-center justify-center gap-0.5 py-1.5 px-3 rounded-lg transition-colors min-w-[52px]",
            isActive ? "text-emerald-brand" : "text-gray-medium hover:text-gray-light"
          )}
          data-testid="bottom-nav-home"
        >
          {({ isActive }) => (
            <>
              <LayoutDashboard className="h-5 w-5" />
              <span className="text-[10px] font-medium leading-tight">Home</span>
              {isActive && <div className="absolute top-0 w-6 h-0.5 rounded-full bg-emerald-brand" />}
            </>
          )}
        </NavLink>

        {/* Leads */}
        <NavLink
          to={navItems[1].path}
          onClick={onNavigate}
          className={({ isActive }) => cn(
            "flex flex-col items-center justify-center gap-0.5 py-1.5 px-3 rounded-lg transition-colors min-w-[52px]",
            isActive ? "text-emerald-brand" : "text-gray-medium hover:text-gray-light"
          )}
          data-testid="bottom-nav-leads"
        >
          {({ isActive }) => (
            <>
              <Target className="h-5 w-5" />
              <span className="text-[10px] font-medium leading-tight">Leads</span>
              {isActive && <div className="absolute top-0 w-6 h-0.5 rounded-full bg-emerald-brand" />}
            </>
          )}
        </NavLink>

        {/* Scan FAB (Center) — must match siblings' min-w so the row stays evenly distributed */}
        <NavLink
          to="/leads/card-scanner"
          onClick={onNavigate}
          className="flex flex-col items-center justify-center -mt-6 min-w-[52px] px-3"
          data-testid="bottom-nav-scan"
        >
          <div className="w-14 h-14 rounded-full bg-emerald-brand flex items-center justify-center shadow-lg shadow-emerald-brand/30 ring-4 ring-charcoal active:scale-95 transition-transform">
            <ScanLine className="h-6 w-6 text-white" />
          </div>
          <span className="text-[10px] font-medium leading-tight text-gray-medium mt-0.5">Scan</span>
        </NavLink>

        {/* Service */}
        <NavLink
          to={navItems[2].path}
          onClick={onNavigate}
          className={({ isActive }) => cn(
            "flex flex-col items-center justify-center gap-0.5 py-1.5 px-3 rounded-lg transition-colors min-w-[52px]",
            isActive ? "text-emerald-brand" : "text-gray-medium hover:text-gray-light"
          )}
          data-testid="bottom-nav-service"
        >
          {({ isActive }) => (
            <>
              <Headphones className="h-5 w-5" />
              <span className="text-[10px] font-medium leading-tight">Service</span>
              {isActive && <div className="absolute top-0 w-6 h-0.5 rounded-full bg-emerald-brand" />}
            </>
          )}
        </NavLink>

        {/* Creative / Contacts */}
        <NavLink
          to={lastNavItem.path}
          onClick={onNavigate}
          className={({ isActive }) => cn(
            "flex flex-col items-center justify-center gap-0.5 py-1.5 px-3 rounded-lg transition-colors min-w-[52px]",
            isActive ? "text-emerald-brand" : "text-gray-medium hover:text-gray-light"
          )}
          data-testid={lastNavItem.testId}
        >
          {({ isActive }) => (
            <>
              <lastNavItem.icon className="h-5 w-5" />
              <span className="text-[10px] font-medium leading-tight">{lastNavItem.label}</span>
              {isActive && <div className="absolute top-0 w-6 h-0.5 rounded-full bg-emerald-brand" />}
            </>
          )}
        </NavLink>
      </div>
    </nav>
  );
};

export default BottomNav;
